import {DateDifference, DateDiffKey} from 'utilities/dates';
import {DeliveryAddress} from 'utilities/location';
import enLocale from 'locales/en.json';

interface TranslationDictionary {
  [key: string]: string | TranslationDictionary;
}

export function translateViaDictionary(
  translations: TranslationDictionary,
  key: string,
  replacements?: {
    [key: string]: string;
  },
) {
  const value = getObjectProperty(translations, key);
  return interpolateValues(value as string, replacements ?? {});
}

function getObjectProperty<
  T extends {
    [key: string]: TK;
  },
  TK,
>(object: T, path: string): TK {
  if (object == null) {
    return object;
  }
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return path.split('.').reduce((acc, key) => acc?.[key], object) as TK;
}

function interpolateValues(
  value: string,
  interpolationValues: {
    [key: string]: string;
  },
) {
  const regex = /{([^}]+)}/gi;
  if (!value) {
    return '';
  }

  return value.replace(regex, (_, key) => {
    if (!(key in interpolationValues) || !interpolationValues[key]) {
      // eslint-disable-next-line no-console
      console.warn(`Key "${key}" not found`, interpolationValues, value);
      return `<${key}>`;
    }
    return interpolationValues[key];
  });
}

export function translate(
  key: string,
  replacements?: {
    [key: string]: string;
  },
) {
  return translateViaDictionary(enLocale, key, replacements);
}

export function formatSingularHoursAndMinutes(
  dateDiff: DateDifference,
): string {
  return (Object.entries(dateDiff) as [DateDiffKey, number][])
    .filter(([, diff]) => diff !== 0)
    .map(([unit, diff]) => `${diff}${translate(`time.${unit}`)}`)
    .join(' ');
}

export function formatExpiryTimeRemaining(dateDiff: DateDifference): string {
  return (Object.entries(dateDiff) as [DateDiffKey, number][])
    .filter(([key, value]) => !(key === 'hour' && value === 0))
    .map(([, value]) => value.toString().padStart(2, '0'))
    .join(':');
}

export function formatDate(date: Date | null, timezone?: string): string {
  if (!date) {
    return '';
  }

  const defaultTimezone = isValidTimezone(timezone)
    ? timezone
    : Intl.DateTimeFormat().resolvedOptions().timeZone;

  return date.toLocaleString('default', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: defaultTimezone,
  });
}

function isValidTimezone(timeZone?: string): boolean {
  if (!timeZone) {
    return false;
  }

  try {
    Intl.DateTimeFormat(undefined, {timeZone});
    return true;
  } catch (err) {
    return false;
  }
}

/**
 * @param address
 */
export function formatDeliveryAddress(address: DeliveryAddress): string {
  // todo: defaultAddress?

  // todo: postalOverride

  if (address.city && address.provinceCode) {
    return `${address.city.trim()}, ${address.provinceCode}`;
  } else if (address.province) {
    return `${address.province}`;
  } else if (address.zip) {
    return address.zip;
  } else {
    return `United States`;
  }
}
